// Device-side API client for the audit viewer. Reads the signed-in JWT from
// sessionStorage and GETs /audit/events with optional filters.
//
// We keep this helper tiny on purpose: the viewer runs in the admin screen,
// which is a tightly-scoped Owner-only surface. If the token is missing or
// the server returns 401/403, we surface that so the viewer can redirect to
// sign-in or show a plain "not authorised" state.

const DEFAULT_API_URL = "http://localhost:3001";

function apiBase(): string {
  return (import.meta.env.VITE_API_URL as string | undefined) ?? DEFAULT_API_URL;
}

export type AuditEvent = {
  id: string;
  occurredAt: string;
  tableSchema: string;
  tableName: string;
  rowId: string | null;
  action: string;
  before: unknown;
  after: unknown;
  details: unknown;
  actorId: string | null;
  deviceId: string | null;
  requestId: string | null;
  rowVersion: string;
};

export type AuditFilters = {
  actorId?: string | undefined;
  deviceId?: string | undefined;
  tableSchema?: string | undefined;
  tableName?: string | undefined;
  rowId?: string | undefined;
  action?: string | undefined;
  from?: string | undefined; // ISO
  to?: string | undefined;
  after?: string | undefined; // cursor (rowVersion of the previous page's last item)
  limit?: number | undefined;
};

export type AuditListResult =
  | { ok: true; events: AuditEvent[]; nextCursor: string | null }
  | { ok: false; status: number; error: string };

export async function listAuditEvents(
  token: string,
  filters: AuditFilters = {},
): Promise<AuditListResult> {
  const url = new URL(`${apiBase()}/audit/events`);
  for (const [k, v] of Object.entries(filters)) {
    if (v !== undefined && v !== null && String(v).length > 0) {
      url.searchParams.set(k, String(v));
    }
  }
  const res = await fetch(url.toString(), {
    headers: { authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({ error: "unknown" }))) as {
      error?: string;
    };
    return { ok: false, status: res.status, error: body.error ?? "unknown" };
  }
  const body = (await res.json()) as { events: AuditEvent[]; nextCursor: string | null };
  return { ok: true, events: body.events, nextCursor: body.nextCursor };
}

export type AuditTokenState = {
  token: string | null;
};

const SESSION_KEY = "betterpos.session_token";

export function readSessionToken(): string | null {
  try {
    return window.sessionStorage.getItem(SESSION_KEY);
  } catch {
    return null;
  }
}

export function writeSessionToken(token: string): void {
  try {
    window.sessionStorage.setItem(SESSION_KEY, token);
  } catch {
    // sessionStorage can throw in private-browsing modes. We treat the
    // viewer as a nice-to-have in that case; it just won't persist.
  }
}

export function clearSessionToken(): void {
  try {
    window.sessionStorage.removeItem(SESSION_KEY);
  } catch {
    /* ignore */
  }
}
